import {produce} from 'immer'

import type {
  Anchor,
  ConnectionOperation,
  CountDelta,
  DocumentAdapterContext,
  DocumentAdapterResult,
  EntityFieldItemApplier,
  EntityFieldItemsUpdater,
  EntityFieldValuesDeltaApplier,
  EntityOperation,
  PendingConnectionOperation,
} from './operation-types'

export type SetFieldsMapper = (fields: Readonly<Record<string, unknown>>) => Record<string, unknown> | undefined
export type EntityFieldMapper = (field: string) => string | undefined

export type RecordAdapterOptions = {
  /** Where this document keeps the global id operations target. Defaults to `id`. */
  identityPath?: string[]
  /** Consumer-side field-name projection, e.g. a row payload's `titleHtml` for `titleHTML`. */
  mapFields?: SetFieldsMapper
  /** Consumer-side semantic scalar field projection, e.g. `comments` to `commentCount`. */
  mapEntityField?: EntityFieldMapper
  /** Applies semantic entity-field item changes after this consumer locates the target entity. */
  applyEntityFieldItem?: EntityFieldItemApplier
  /** Applies related semantic field deltas atomically after locating the target entity. */
  applyEntityFieldValuesDelta?: EntityFieldValuesDeltaApplier
  /** Applies semantic capped-list changes after locating the target entity. */
  updateEntityFieldItems?: EntityFieldItemsUpdater
}

export function createRecordDocumentAdapter(options: RecordAdapterOptions = {}) {
  const identityIndexes = new WeakMap<object, Set<string>>()

  return function applyRecordDocument<T>(
    document: T,
    change: PendingConnectionOperation,
    context: DocumentAdapterContext = {},
  ): DocumentAdapterResult<T> {
    let operation = change.operation
    if ('id' in operation) {
      if (document === null || typeof document !== 'object') return undefined
      let identities = identityIndexes.get(document)
      if (!identities) {
        identities = collectIdentities(document, options.identityPath)
        identityIndexes.set(document, identities)
      }
      if (!identities.has(operation.id)) return undefined
      const projected = projectEntityOperation(operation, options.mapFields, options.mapEntityField)
      if (!projected) return undefined
      operation = projected
    } else if (!collectIdentities(document).has(change.ownerId)) {
      return undefined
    }

    // Keep the existing record engine intact until hook and settlement integration.
    // The integrated engine will collect structural affectedness during this fold.
    return {
      document: applyOperation(document, change.ownerId, operation, {
        ...options,
        ...context,
        operationId: change.operationId,
      }),
    }
  }
}

export const recordDocumentAdapter = createRecordDocumentAdapter()

/**
 * Tags a real node folded into `base` on add-confirmation with the client temp id it
 * reconciles, so a still-pending add operation (node id = temp id) recognises it despite
 * the different server id — no duplicate row or double count during settle. A later real
 * refetch replaces it. Only needed by "create new issue" (temp id ≠ real id); "add
 * existing" matches by id directly.
 */
export const OPTIMISTIC_KEY = '__optimisticKey'

/**
 * How a document stores the identity a `setFields` operation targets. A producer publishes the
 * entity's global id and nothing about the shape it lands in; the document that receives the
 * operation knows whether it keys nodes as `id` (a GraphQL selection) or under another selected
 * property such as `globalId` (a denormalized row payload). So identity is supplied here, by the
 * consumer applying the operation, rather than carried on the operation — the same rule that puts
 * field-name projection on the consumer. Defaults to `id`.
 *
 * Structural add/remove/move operations are unaffected: they resolve their owner and their member
 * nodes by `id`, because they address a GraphQL-shaped connection. Giving them an alternate
 * identity needs node-level matching too (`OPTIMISTIC_KEY` reconciliation, anchors), which no
 * consumer needs yet.
 */
export type ApplyOptions = {
  identityPath?: string[]
  applyEntityFieldItem?: EntityFieldItemApplier
  applyEntityFieldValuesDelta?: EntityFieldValuesDeltaApplier
  updateEntityFieldItems?: EntityFieldItemsUpdater
  operationId?: string
  incorporateOperation?: boolean
}

/**
 * Applies one operation to `doc`, returning a new document (immer structural sharing —
 * unrelated cache entries keep their identity). Pure: never mutates its input.
 */
export function applyOperation<T>(
  doc: T,
  ownerId: string,
  operation: ConnectionOperation,
  {
    identityPath = ID_PATH,
    applyEntityFieldItem,
    applyEntityFieldValuesDelta,
    updateEntityFieldItems,
    operationId,
    incorporateOperation,
  }: ApplyOptions = {},
): T {
  return produce(doc, draft => {
    if (operation.type === 'setFields') {
      forEachObjectWithIdentity(draft, identityPath, operation.id, node => {
        overlayFields(node, operation.fields)
        markOperationIncorporated(node, operationId, incorporateOperation)
      })
      return
    }
    if (operation.type === 'upsertEntityFieldItem' || operation.type === 'removeEntityFieldItem') {
      if (!applyEntityFieldItem) return
      forEachObjectWithIdentity(draft, identityPath, operation.id, owner => applyEntityFieldItem(owner, operation))
      return
    }
    if (operation.type === 'deltaEntityField') {
      forEachObjectWithIdentity(draft, identityPath, operation.id, owner => {
        if (operationId && incorporatedOperationIds(owner).includes(operationId)) return
        const current = owner[operation.field]
        if (typeof current !== 'number' || !Number.isFinite(current) || !Number.isFinite(operation.delta)) return
        let next = current + operation.delta
        if (operation.clamp?.min !== undefined) next = Math.max(operation.clamp.min, next)
        if (operation.clamp?.max !== undefined) next = Math.min(operation.clamp.max, next)
        owner[operation.field] = next
        if (operationId && incorporateOperation) {
          owner[INCORPORATED_OPERATION_IDS] = [...incorporatedOperationIds(owner), operationId]
        }
      })
      return
    }
    if (operation.type === 'deltaEntityFieldValues') {
      if (!applyEntityFieldValuesDelta) return
      forEachObjectWithIdentity(draft, identityPath, operation.id, owner => {
        if (operationId && incorporatedOperationIds(owner).includes(operationId)) return
        if (applyEntityFieldValuesDelta(owner, operation)) {
          markOperationIncorporated(owner, operationId, incorporateOperation)
        }
      })
      return
    }
    if (operation.type === 'updateEntityFieldItems') {
      if (!updateEntityFieldItems) return
      forEachObjectWithIdentity(draft, identityPath, operation.id, owner => {
        if (operationId && incorporatedOperationIds(owner).includes(operationId)) return
        if (updateEntityFieldItems(owner, operation)) {
          markOperationIncorporated(owner, operationId, incorporateOperation)
        }
      })
      return
    }

    // Structural operations address a GraphQL-shaped connection, so the owner is always matched
    // by `id` (see `ApplyOptions`).
    forEachObjectWithIdentity(draft, ID_PATH, ownerId, owner => {
      if (operationId && incorporatedOperationIds(owner).includes(operationId)) return
      switch (operation.type) {
        case 'removeFromConnection': {
          const representations = resolveConnectionRepresentations(owner, operation.connectionPath)
          let removed = false
          for (const rep of representations) {
            const index = rep.entries.findIndex(entry => nodeIdOf(rep.nodeOf(entry)) === operation.nodeId)
            if (index === -1) continue
            rep.entries.splice(index, 1)
            removed = true
          }
          // Apply counts when we removed the node from a listed representation, or when the
          // consumer is count-only (no representation to reconcile against). Skip when the node
          // was already absent (avoids a double-decrement if a refetch already incorporated it).
          if (representations.length === 0 || removed) applyCounts(owner, operation.counts)
          break
        }
        case 'addToConnection': {
          const correlationKey = optimisticKeyOf(operation.node)
          const addedNodeId = nodeIdOf(operation.node)
          const representations = resolveConnectionRepresentations(owner, operation.connectionPath, true)
          let alreadyPresent = false
          for (const rep of representations) {
            // Match on the correlation key (a create's confirmed node keeps its temp id) or the
            // raw id: an entry already present means a settle folded it in while this add was
            // pending — re-inserting would duplicate the row and double-count.
            const existingEntry =
              addedNodeId != null
                ? rep.entries.find(
                    entry =>
                      optimisticKeyOf(rep.nodeOf(entry)) === correlationKey ||
                      nodeIdOf(rep.nodeOf(entry)) === addedNodeId,
                  )
                : undefined
            if (existingEntry != null) {
              alreadyPresent = true
              // A create's confirmed node carries its temp id. If a live update already inserted
              // the real node untagged, copy the temp id onto it so the still-pending temp add
              // recognises it as the same row.
              const incomingKey = getAtPath(operation.node, [OPTIMISTIC_KEY])
              const existingNode = asRecord(rep.nodeOf(existingEntry))
              if (incomingKey != null && existingNode && getAtPath(existingNode, [OPTIMISTIC_KEY]) == null) {
                existingNode[OPTIMISTIC_KEY] = incomingKey
              }
              continue
            }
            rep.entries.splice(
              anchorIndex(rep.entries, operation.anchor, rep.nodeOf),
              0,
              rep.wrap(structuredClone(operation.node)),
            )
          }
          // Count-only consumers (no representation) can't dedupe, so counting on a later fold
          // would double-count; only apply counts for a genuinely new insert. When the node was
          // already present in one representation, inserting it into another just reconciles the
          // views — the count already reflects it, so don't re-apply.
          if (representations.length === 0 || !alreadyPresent) applyCounts(owner, operation.counts)
          break
        }
        case 'moveInConnection': {
          const representations = resolveConnectionRepresentations(owner, operation.connectionPath)
          if (representations.length === 0) return
          // Moving a node relative to itself is a no-op. Guard before removing it, otherwise the
          // anchor lookup misses (the node is already gone) and it falls back to a boundary.
          const {anchor} = operation
          if (
            ('after' in anchor && anchor.after === operation.nodeId) ||
            ('before' in anchor && anchor.before === operation.nodeId)
          )
            return
          for (const rep of representations) {
            const from = rep.entries.findIndex(entry => nodeIdOf(rep.nodeOf(entry)) === operation.nodeId)
            if (from === -1) continue
            const [moved] = rep.entries.splice(from, 1)
            rep.entries.splice(anchorIndex(rep.entries, operation.anchor, rep.nodeOf), 0, moved)
          }
          break
        }
      }
      markOperationIncorporated(owner, operationId, incorporateOperation)
    })
  })
}

/** Applies a consumer's field-name projection, dropping the operation when it maps to nothing. */
export function projectEntityOperation(
  operation: EntityOperation,
  mapSetFields: SetFieldsMapper | undefined,
  mapEntityField: EntityFieldMapper | undefined,
): EntityOperation | undefined {
  if (operation.type === 'setFields' && mapSetFields) {
    const fields = mapSetFields(operation.fields)
    if (!fields) return undefined
    return {...operation, fields}
  }
  if (operation.type === 'deltaEntityField' && mapEntityField) {
    const field = mapEntityField(operation.field)
    return field ? {...operation, field} : undefined
  }
  return operation
}

/** Removes settlement-only correlation metadata after the matching mutation leaves `pending`. */
export function removeLegacyIncorporatedOperation<T>(
  doc: T,
  entityId: string,
  operationId: string,
  identityPath: string[] = ID_PATH,
): T {
  return produce(doc, draft => {
    forEachObjectWithIdentity(draft, identityPath, entityId, entity => {
      const remaining = incorporatedOperationIds(entity).filter(id => id !== operationId)
      if (remaining.length === 0) delete entity[INCORPORATED_OPERATION_IDS]
      else entity[INCORPORATED_OPERATION_IDS] = remaining
    })
  })
}

// Hook and settlement callers retain their original application/cleanup paths until
// custom adapter registration and its abandonment lifecycle can be installed together.
export function applyOperations<T>(
  base: T,
  pendingOperations: PendingConnectionOperation[],
  options: ApplyOptions = {},
): T {
  return pendingOperations.reduce<T>(
    (doc, {ownerId, operation, operationId}) => applyOperation(doc, ownerId, operation, {...options, operationId}),
    base,
  )
}

/** Pass undefined for entityId to prune the shared marker throughout a differently shaped document. */
export function removeIncorporatedOperation<T>(
  doc: T,
  entityId: string | undefined,
  operationId: string,
  identityPath: string[] = ID_PATH,
): T {
  return produce(doc, draft => {
    forEachObjectWithIdentity(draft, identityPath, entityId, entity => {
      if (!hasIncorporatedOperation(entity, operationId)) return
      const remaining = incorporatedOperationIds(entity).filter(id => id !== operationId)
      if (remaining.length === 0) delete entity[INCORPORATED_OPERATION_IDS]
      else entity[INCORPORATED_OPERATION_IDS] = remaining
    })
  })
}

// ---------------------------------------------------------------------------
// Internal helpers. Generic cache-tree utilities, duplicated rather than imported from the
// bridge package so this permanent package doesn't depend on code that gets deleted.
// ---------------------------------------------------------------------------

type CacheObject = Record<string, unknown>

const NODES_KEY = 'nodes'
const EDGES_KEY = 'edges'
const EDGE_NODE_KEY = 'node'
const ID_PATH = ['id']
const INCORPORATED_OPERATION_IDS = '__optimisticIncorporatedOperationIds'

function incorporatedOperationIds(node: CacheObject): string[] {
  const value = node[INCORPORATED_OPERATION_IDS]
  return Array.isArray(value) && value.every(id => typeof id === 'string') ? value : []
}

export function hasIncorporatedOperation(node: CacheObject, operationId?: string): boolean {
  return operationId !== undefined && incorporatedOperationIds(node).includes(operationId)
}

export function markOperationIncorporated(
  node: CacheObject,
  operationId?: string,
  incorporateOperation?: boolean,
): void {
  if (operationId && incorporateOperation) {
    const incorporated = incorporatedOperationIds(node)
    if (!incorporated.includes(operationId)) node[INCORPORATED_OPERATION_IDS] = [...incorporated, operationId]
  }
}

/**
 * One of a connection's mutable entry lists plus accessors. `nodeOf` reads the node out of an
 * entry and `wrap` builds an entry around a node — both identity for the `nodes` shape,
 * `.node` / `{node}` for the `edges` shape.
 */
type ConnectionEntries = {
  entries: unknown[]
  nodeOf: (entry: unknown) => unknown
  wrap: (node: unknown) => unknown
}

/**
 * Every node-list representation a connection exposes. The engine drives both a plain **`nodes`**
 * array and a Relay **`edges[].node`** array; a query usually selects one, but if it selects both,
 * a structural op must update both or the two views contradict each other (and an edge-based
 * renderer would miss a `nodes`-only change). Returns an empty array for a count-only connection.
 */
function resolveConnectionRepresentations(
  owner: CacheObject,
  connectionPath: string[],
  materializeEmptyNodes = false,
): ConnectionEntries[] {
  const connection = asRecord(getAtPath(owner, connectionPath))
  if (!connection) return []
  const representations: ConnectionEntries[] = []
  // An empty connection can come back as `nodes: null`. It's still a listed connection, so
  // materialise it when adding — otherwise it reads as count-only and the row never lands.
  // A consumer that selected no `nodes` key at all is genuinely count-only and is left alone.
  if (materializeEmptyNodes && NODES_KEY in connection && connection[NODES_KEY] == null) {
    connection[NODES_KEY] = []
  }
  if (Array.isArray(connection[NODES_KEY])) {
    representations.push({entries: connection[NODES_KEY], nodeOf: entry => entry, wrap: node => node})
  }
  if (Array.isArray(connection[EDGES_KEY])) {
    // An optimistic add wraps the node as `{node}` only: a node-centric operation can't
    // synthesize per-edge fields such as Relay's `cursor` (a server-issued pagination token).
    // The optimistic edge is transient — the confirmed value replaces it on settle/refetch — so
    // any edge-level fields are absent only for that window. A consumer that renders edge-level
    // fields on an optimistic row should select `nodes` instead.
    representations.push({
      entries: connection[EDGES_KEY],
      nodeOf: entry => getAtPath(entry, [EDGE_NODE_KEY]),
      wrap: node => ({[EDGE_NODE_KEY]: node}),
    })
  }
  return representations
}

function asRecord(value: unknown): CacheObject | undefined {
  return value !== null && typeof value === 'object' ? (value as CacheObject) : undefined
}

// Keys that could pollute Object.prototype if they arrive from mutation input.
function isUnsafeKey(key: string): boolean {
  return key === '__proto__' || key === 'constructor' || key === 'prototype'
}

function isPlainObject(value: unknown): value is CacheObject {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === null || prototype === Object.prototype
}

function getAtPath(node: unknown, path: string[]): unknown {
  let current: unknown = node
  for (const segment of path) {
    const record = asRecord(current)
    if (!record || isUnsafeKey(segment) || !Object.prototype.hasOwnProperty.call(record, segment)) return undefined
    current = record[segment]
  }
  return current
}

function setAtPath(node: unknown, path: string[], value: unknown): void {
  let parent = asRecord(node)
  for (const segment of path.slice(0, -1)) {
    if (!parent) return
    parent = asRecord(parent[segment])
  }
  const leaf = path[path.length - 1]
  // The `hasOwnProperty` guard keeps this to properties the object already owns (all
  // callers target an existing numeric count) and doubles as a prototype-pollution barrier;
  // `isUnsafeKey` is belt-and-suspenders.
  if (parent && leaf !== undefined && !isUnsafeKey(leaf) && Object.prototype.hasOwnProperty.call(parent, leaf)) {
    parent[leaf] = value
  }
}

/** Visits every object in the tree whose identity at `identityPath` equals `id` (the same id
 *  can recur in one document, e.g. an issue in both `blockedBy` and `subIssues`). */
function forEachObjectWithIdentity(
  node: unknown,
  identityPath: string[],
  id: string | undefined,
  onMatch: (obj: CacheObject) => void,
): void {
  // Collect matches from the pre-operation tree, then run the callback. Visiting inline would
  // descend into a node the callback just inserted; if that node embeds another copy of the
  // owner (a valid denormalized shape), it would re-match and insert forever. Copies that
  // already existed are still all visited.
  const matches: CacheObject[] = []
  collectObjectsWithIdentity(node, identityPath, id, matches)
  for (const obj of matches) onMatch(obj)
}

function collectObjectsWithIdentity(
  node: unknown,
  identityPath: string[],
  id: string | undefined,
  out: CacheObject[],
): void {
  if (Array.isArray(node)) {
    for (const item of node) collectObjectsWithIdentity(item, identityPath, id, out)
    return
  }
  const obj = asRecord(node)
  if (!obj) return
  if (id === undefined || getAtPath(obj, identityPath) === id) out.push(obj)
  for (const value of Object.values(obj)) collectObjectsWithIdentity(value, identityPath, id, out)
}

function nodeIdOf(node: unknown): unknown {
  return getAtPath(node, ID_PATH)
}

/**
 * Every identity value present in `doc` at `identityPath`. `useOptimisticData` uses this to
 * discard operations targeting entities this document doesn't contain, so an unrelated mutation
 * elsewhere on the page costs a set lookup instead of a full document walk per operation.
 */
export function collectIdentities(doc: unknown, identityPath: string[] = ID_PATH): Set<string> {
  const identities = new Set<string>()
  visit(doc)
  return identities

  function visit(node: unknown): void {
    if (Array.isArray(node)) {
      for (const item of node) visit(item)
      return
    }
    const obj = asRecord(node)
    if (!obj) return
    const identity = getAtPath(obj, identityPath)
    if (typeof identity === 'string') identities.add(identity)
    for (const value of Object.values(obj)) visit(value)
  }
}

/**
 * The identity an optimistic-add dedupes on: a node's `OPTIMISTIC_KEY` if present (a real
 * node folded in on confirmation, tagged with the temp id it replaces), else its `id`. So a
 * pending temp op becomes a no-op once `base` holds that temp id or a node tagged with it.
 */
function optimisticKeyOf(node: unknown): unknown {
  const key = getAtPath(node, [OPTIMISTIC_KEY])
  return key != null ? key : nodeIdOf(node)
}

function deepMergePlainObjects(target: CacheObject, source: CacheObject): CacheObject {
  const merged: CacheObject = {...target}
  for (const key of Object.keys(source)) {
    if (isUnsafeKey(key)) continue
    const sourceValue = source[key]
    const targetValue = merged[key]
    merged[key] =
      isPlainObject(targetValue) && isPlainObject(sourceValue)
        ? deepMergePlainObjects(targetValue, sourceValue)
        : sourceValue
  }
  return merged
}

/** Resolves an anchor to an insertion index in `entries` (which must already exclude the entry
 *  being placed). `nodeOf` reads the node from an entry (identity for a `nodes` connection,
 *  `.node` for an `edges` one). An absent `after`/`before` target falls back to append/prepend. */
function anchorIndex(entries: unknown[], anchor: Anchor, nodeOf: (entry: unknown) => unknown = entry => entry): number {
  if ('at' in anchor) return anchor.at === 'start' ? 0 : entries.length

  const targetId = 'after' in anchor ? anchor.after : anchor.before
  const index = entries.findIndex(entry => nodeIdOf(nodeOf(entry)) === targetId)
  if ('after' in anchor) return index === -1 ? entries.length : index + 1
  return index === -1 ? 0 : index
}

function overlayFields(node: CacheObject, fields: CacheObject): void {
  for (const key of Object.keys(fields)) {
    // Only overlay a field the node already selected (a node that fetched `titleHTML` but
    // not `title` is left untouched for `title`).
    if (!Object.prototype.hasOwnProperty.call(node, key)) continue
    const next = fields[key]
    const current = node[key]
    node[key] = isPlainObject(current) && isPlainObject(next) ? deepMergePlainObjects(current, next) : next
  }
}

function applyCounts(owner: CacheObject, counts?: CountDelta[]): void {
  for (const {path, delta} of counts ?? []) {
    const current = getAtPath(owner, path)
    if (typeof current === 'number') setAtPath(owner, path, Math.max(0, current + delta))
  }
}
